import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Spinner, Alert, Table, Badge, Button, ListGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faCubes, faListUl, faMagnifyingGlass, faLock, faTimes, faCheck, faPlus, faTrash, faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getKit, getKitCatalog, createKit, updateKit } from '../services/api';
import '../styles/Cart.css';
import '../styles/modal-shared.css';

/*
 * Creating or changing a kit's bill of materials: a name, a description, and
 * which of the customer's own catalog items go into one kit, and how many of
 * each. A kit with open orders cannot be changed until they ship or are
 * rejected; the server enforces that, and this page explains it up front.
 */

const NAME_MAX = 100;
const DESC_MAX = 255;
const SEARCH_DELAY_MS = 300;

const fmtQty = (n) => Number(n || 0).toLocaleString();

export default function KitEditor() {
  const { kitId, tenantPrefix } = useParams();
  const isNew = !kitId;
  const navigate = useNavigate();
  const link = useCallback((path) => (tenantPrefix ? `/${tenantPrefix}${path}` : path), [tenantPrefix]);

  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState('');
  const [kitName, setKitName] = useState('');
  const [kitDesc, setKitDesc] = useState('');
  const [components, setComponents] = useState([]);
  const [openOrders, setOpenOrders] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const locked = !isNew && openOrders > 0;

  // Load the kit being edited.
  useEffect(() => {
    if (isNew) return undefined;
    let cancelled = false;
    getKit(kitId)
      .then((kit) => {
        if (cancelled) return;
        setKitName(kit.kitName || '');
        setKitDesc(kit.kitDesc || '');
        setOpenOrders(kit.openOrders || 0);
        setComponents(kit.components.map((c) => ({
          idCatalog: c.idCatalog, itemNumber: c.itemNumber, itemName: c.itemName,
          unit: c.unit, available: c.available, qty: String(c.qtyPer),
        })));
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.response?.status === 404 ? 'This kit was not found.' : 'Failed to load the kit. Please try again.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isNew, kitId]);

  // Search the catalog as the user types.
  useEffect(() => {
    const term = query.trim();
    if (!term) { setResults([]); setSearchError(''); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      setSearchError('');
      try {
        const items = await getKitCatalog(term);
        if (!cancelled) setResults(items);
      } catch {
        if (!cancelled) setSearchError('Search failed. Please try again.');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SEARCH_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);

  const inKit = useMemo(() => new Set(components.map((c) => c.idCatalog)), [components]);

  const addItem = (item) => {
    if (inKit.has(item.idCatalog)) return;
    setComponents((prev) => [...prev, { ...item, qty: '1' }]);
  };
  const setQty = (idCatalog, qty) => setComponents((prev) => prev.map((c) => (c.idCatalog === idCatalog ? { ...c, qty } : c)));
  const removeItem = (idCatalog) => setComponents((prev) => prev.filter((c) => c.idCatalog !== idCatalog));

  // What the recipe as it stands could build from current stock.
  const rows = components.map((c) => {
    const qty = Number(c.qty);
    const valid = Number.isInteger(qty) && qty > 0;
    return { ...c, valid, kitsAllowed: valid ? Math.floor((c.available || 0) / qty) : null };
  });
  const allValid = rows.length > 0 && rows.every((r) => r.valid);
  const buildable = allValid ? Math.min(...rows.map((r) => r.kitsAllowed)) : null;
  const limiting = allValid ? rows.filter((r) => r.kitsAllowed === buildable) : [];

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!kitName.trim()) { setError('Give the kit a name.'); return; }
    if (rows.length === 0) { setError('Add at least one component.'); return; }
    if (!allValid) { setError('Every quantity per kit must be a whole number above zero.'); return; }

    const payload = {
      kitName: kitName.trim(),
      kitDesc: kitDesc.trim(),
      components: rows.map((r) => ({ idCatalog: r.idCatalog, qty: Number(r.qty) })),
    };
    setSaving(true);
    try {
      if (isNew) await createKit(payload);
      else await updateKit(kitId, payload);
      toast.success(isNew ? `Kit ${payload.kitName} created` : `Kit ${payload.kitName} saved`);
      navigate(link('/kits'));
    } catch (err) {
      const data = err.response?.data || {};
      if (err.response?.status === 409 && data.openOrders) setOpenOrders(data.openOrders);
      setError(data.message || 'Failed to save the kit. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Loading kit…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="checkout-page"><div className="checkout-page-inner">
        <Alert variant="danger" className="mt-3">{loadError}</Alert>
        <button type="button" className="detail-btn detail-btn--ghost" onClick={() => navigate(link('/kits'))}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to kits
        </button>
      </div></div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-page-inner">
        <div className="checkout-page-header">
          <button className="checkout-back-btn" onClick={() => navigate(link('/kits'))} title="Back to kits">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <h1 className="checkout-page-title">{isNew ? 'New Kit' : `Edit Kit: ${kitName}`}</h1>
            <p className="checkout-page-subtitle">A kit is built from items already in your catalog.</p>
          </div>
        </div>

        {locked && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: '0.85rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FontAwesomeIcon icon={faLock} />
            This kit has {openOrders} open order{openOrders === 1 ? '' : 's'}, so it can&apos;t be changed until
            {openOrders === 1 ? ' it ships or is rejected' : ' they ship or are rejected'}.
          </div>
        )}
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: '0.82rem', color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FontAwesomeIcon icon={faCircleInfo} />
            {error}
          </div>
        )}

        <Form onSubmit={handleSave}>
          {/* Everything editable sits in one fieldset, so a locked kit is read-only; Cancel stays usable. */}
          <div className="checkout-layout">
            {/* ── LEFT COLUMN ── */}
            <fieldset disabled={locked || saving} style={{ minWidth: 0 }}>
              <div className="checkout-panel">
                <div className="checkout-panel-header">
                  <div className="checkout-panel-icon"><FontAwesomeIcon icon={faCubes} /></div>
                  <h6>Kit</h6>
                </div>
                <div className="checkout-panel-body">
                  <div className="row g-2">
                    <div className="col-12 col-md-5">
                      <div className="co-label">Kit Name <span style={{ color: '#ef4444' }}>*</span></div>
                      <Form.Control size="sm" type="text" value={kitName} onChange={(e) => setKitName(e.target.value)}
                        maxLength={NAME_MAX} isInvalid={!!error && !kitName.trim()} style={{ borderRadius: 8 }} />
                    </div>
                    <div className="col-12 col-md-7">
                      <div className="co-label">Description</div>
                      <Form.Control size="sm" type="text" value={kitDesc} onChange={(e) => setKitDesc(e.target.value)}
                        maxLength={DESC_MAX} placeholder="Optional" style={{ borderRadius: 8 }} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="checkout-panel">
                <div className="checkout-panel-header">
                  <div className="checkout-panel-icon"><FontAwesomeIcon icon={faListUl} /></div>
                  <h6>Components <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.75rem' }}>({components.length})</span></h6>
                </div>
                <div className="checkout-panel-body">
                  <div className="co-label">Add an item from your catalog</div>
                  <div style={{ position: 'relative' }}>
                    <FontAwesomeIcon icon={faMagnifyingGlass} style={{ position: 'absolute', left: 10, top: 9, color: '#94a3b8', fontSize: '0.8rem' }} />
                    <Form.Control size="sm" type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search by SKU or description…" style={{ borderRadius: 8, paddingLeft: 30 }} aria-label="Search your catalog" />
                  </div>
                  {(searching || searchError || results.length > 0 || query.trim()) && (
                    <div style={{ maxHeight: 240, overflowY: 'auto', marginTop: 6, border: '1px solid #e2e8f0', borderRadius: 8 }}>
                      {searching && results.length === 0 && <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>}
                      {searchError && <div className="px-2 py-2 small text-danger">{searchError}</div>}
                      {!searching && !searchError && results.length === 0 && query.trim() && (
                        <div className="px-2 py-2 small text-muted">No items match “{query.trim()}”.</div>
                      )}
                      <ListGroup variant="flush">
                        {results.map((item) => {
                          const added = inKit.has(item.idCatalog);
                          return (
                            <ListGroup.Item key={item.idCatalog} className="d-flex align-items-center gap-2 py-1 px-2" style={{ fontSize: '0.82rem' }}>
                              <code style={{ minWidth: 90 }}>{item.itemNumber}</code>
                              <span className="flex-grow-1" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.itemName || '—'}</span>
                              <span className="text-muted" style={{ whiteSpace: 'nowrap' }}>{fmtQty(item.available)} {item.unit || ''} available</span>
                              <Button size="sm" variant={added ? 'outline-secondary' : 'outline-primary'} disabled={added} onClick={() => addItem(item)} style={{ minWidth: 70 }}>
                                {added ? 'Added' : <><FontAwesomeIcon icon={faPlus} className="me-1" />Add</>}
                              </Button>
                            </ListGroup.Item>
                          );
                        })}
                      </ListGroup>
                    </div>
                  )}

                  {components.length === 0 ? (
                    <div className="text-muted small mt-3">No components yet. Search above to add items.</div>
                  ) : (
                    <Table size="sm" bordered className="mt-3 mb-0 bg-white" style={{ fontSize: '0.8rem' }}>
                      <thead>
                        <tr>
                          {['SKU', 'Description', 'Unit', 'Available', 'Per Kit', 'Kits It Allows', ''].map((h, i) => (
                            <th key={h || i} className={i >= 3 && i <= 5 ? 'text-end' : ''} style={{ background: '#e9ecef', padding: '5px 8px', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.idCatalog} style={allValid && r.kitsAllowed === buildable ? { background: '#fff7e6' } : undefined}>
                            <td style={{ padding: '4px 8px' }}><code>{r.itemNumber}</code></td>
                            <td style={{ padding: '4px 8px' }}>{r.itemName || '—'}</td>
                            <td style={{ padding: '4px 8px' }}>{r.unit || '—'}</td>
                            <td className="text-end" style={{ padding: '4px 8px' }}>{fmtQty(r.available)}</td>
                            <td className="text-end" style={{ padding: '2px 6px', width: 96 }}>
                              <Form.Control size="sm" type="number" min={1} step={1} value={r.qty}
                                onChange={(e) => setQty(r.idCatalog, e.target.value)} isInvalid={!r.valid}
                                style={{ textAlign: 'right' }} aria-label={`Quantity of ${r.itemNumber} per kit`} />
                            </td>
                            <td className="text-end" style={{ padding: '4px 8px' }}>{r.valid ? fmtQty(r.kitsAllowed) : '—'}</td>
                            <td className="text-center" style={{ padding: '2px 4px', width: 40 }}>
                              <Button size="sm" variant="link" className="text-danger p-0" onClick={() => removeItem(r.idCatalog)} title={`Remove ${r.itemNumber}`}>
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              </div>
            </fieldset>

            {/* ── RIGHT COLUMN ── */}
            <div>
              <div className="checkout-panel">
                <div className="checkout-panel-header">
                  <div className="checkout-panel-icon"><FontAwesomeIcon icon={faCubes} /></div>
                  <h6>Summary</h6>
                </div>
                <div className="checkout-panel-body" style={{ fontSize: '0.85rem' }}>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Components</span>
                    <strong>{fmtQty(components.length)}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Units per kit</span>
                    <strong>{allValid ? fmtQty(rows.reduce((n, r) => n + Number(r.qty), 0)) : '—'}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Can build now</span>
                    {buildable === null
                      ? <strong>—</strong>
                      : <Badge bg={buildable > 0 ? 'success' : 'secondary'} style={{ fontSize: '0.85rem' }}>{fmtQty(buildable)}</Badge>}
                  </div>
                  {limiting.length > 0 && (
                    <div className="small text-muted">
                      Limited by {limiting.slice(0, 3).map((r) => r.itemNumber).join(', ')}{limiting.length > 3 ? ` +${limiting.length - 3}` : ''}
                    </div>
                  )}
                  <div className="small text-muted mt-2">
                    Based on stock available now across all warehouses.
                  </div>
                </div>
              </div>

              <div className="checkout-footer">
                <button type="button" className="detail-btn detail-btn--ghost" onClick={() => navigate(link('/kits'))}>
                  <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button type="submit" className="detail-btn detail-btn--primary" disabled={saving || locked}>
                  {saving
                    ? <><Spinner animation="border" size="sm" /> Saving…</>
                    : <><FontAwesomeIcon icon={faCheck} /> {isNew ? 'Create Kit' : 'Save Kit'}</>}
                </button>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
}
